<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Resumo do aluno</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn type="button" elevation="0" color="primary" dark :to="`/alunos/${studentId}`">
                            Editar aluno
                        </v-btn>
                        <v-btn type="button" elevation="0" color="primary" outlined to="/alunos">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="loading">
            <v-col cols="12">
                <v-skeleton-loader type="article, table, actions"></v-skeleton-loader>
            </v-col>
        </v-row>

        <template v-else>
            <v-row>
                <v-col cols="12">
                    <v-card class="student-summary-hero">
                        <div class="student-hero-main">
                            <v-avatar class="student-hero-avatar" size="92">
                                <v-img v-if="student.document_url" :src="student.document_url"></v-img>
                                <span v-else>{{ studentInitials }}</span>
                            </v-avatar>
                            <div>
                                <span class="student-summary-eyebrow">Aluno</span>
                                <h1>{{ student.name || "Aluno sem nome" }}</h1>
                                <p>
                                    {{ student.registration || "Sem matrícula" }}
                                    <template v-if="student.email"> · {{ student.email }}</template>
                                </p>
                            </div>
                        </div>

                        <div class="student-summary-meta">
                            <div>
                                <small>Status escolar</small>
                                <strong>{{ enrollmentStatusLabel }}</strong>
                            </div>
                            <div>
                                <small>Localização</small>
                                <strong>{{ currentStatusLabel }}</strong>
                            </div>
                            <div>
                                <small>Turma</small>
                                <strong>{{ classData.name || "Sem turma ativa" }}</strong>
                            </div>
                            <div>
                                <small>Ano vigente</small>
                                <strong>{{ schoolYear.year || "N/A" }}</strong>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="exitPreference && (!exitPreference.responsible_enabled || !exitPreference.transporter_enabled)">
                <v-col cols="12">
                    <v-alert type="warning" outlined>
                        <strong>{{ exitPreferenceLabel }}</strong>
                        <span class="summary-exit-note">Considere esta preferência antes de realizar uma liberação manual.</span>
                        <small v-if="exitPreference.disabled_at" class="summary-exit-note">
                            Alterado em {{ $moment(exitPreference.disabled_at).format("DD/MM/YYYY [às] HH:mm") }}
                            · {{ exitPreference.change_source || "configuração da escola" }}
                        </small>
                    </v-alert>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <div class="summary-kpi-grid">
                        <v-card class="summary-kpi" v-for="item in kpis" :key="item.label">
                            <v-icon>{{ item.icon }}</v-icon>
                            <div>
                                <strong>{{ item.value }}</strong>
                                <span>{{ item.label }}</span>
                            </div>
                        </v-card>
                    </div>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" lg="4">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="card-group-title">Matrícula</v-card-title>
                        <v-card-text>
                            <div class="summary-info-list">
                                <div>
                                    <span>Turma</span>
                                    <strong>{{ classData.name || "N/A" }}</strong>
                                </div>
                                <div>
                                    <span>Ensino</span>
                                    <strong>{{ classEducationLabel }}</strong>
                                </div>
                                <div>
                                    <span>Data de matrícula</span>
                                    <strong>{{ enrollmentDateLabel }}</strong>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="4">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="card-group-title">Frequência</v-card-title>
                        <v-card-text>
                            <div class="attendance-overview">
                                <strong>{{ stats.attendance_percent || 0 }}%</strong>
                                <span>presença registrada</span>
                            </div>
                            <div class="attendance-bars">
                                <div>
                                    <span>Presenças</span>
                                    <strong>{{ stats.attendance_present || 0 }}</strong>
                                </div>
                                <v-progress-linear :value="stats.attendance_percent || 0" height="7" rounded color="primary"></v-progress-linear>
                                <div>
                                    <span>Faltas</span>
                                    <strong>{{ stats.attendance_absent || 0 }}</strong>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="4">
                    <v-card class="card-form-outlined summary-panel">
                        <v-card-title class="card-group-title">Vínculos</v-card-title>
                        <v-card-text>
                            <div class="summary-chip-list" v-if="answerables.length || transporters.length">
                                <v-chip v-for="answerable in answerables" :key="`answerable-${answerable.id}`" label>
                                    <v-icon left small>mdi-human-male-boy</v-icon>
                                    {{ answerable.name }}
                                </v-chip>
                                <v-chip v-for="transporter in transporters" :key="`transporter-${transporter.id}`" label>
                                    <v-icon left small>mdi-bus-school</v-icon>
                                    {{ transporter.name }}
                                </v-chip>
                            </div>
                            <span v-else>Nenhum responsável ou transporte vinculado.</span>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" lg="7">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Movimentações de acesso</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="movementHeaders"
                                :items="movements"
                                :items-per-page="8"
                                hide-default-footer
                                class="app-table"
                                no-data-text="Nenhuma entrada ou saída registrada"
                            >
                                <template v-slot:[`item.code`]="{ item }">
                                    <v-chip label small :color="movementColor(item.code)" text-color="white">
                                        {{ movementLabel(item.code) }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.equipment`]="{ item }">
                                    <div class="stacked-cell">
                                        <strong>{{ item.equipment_name || "Equipamento não identificado" }}</strong>
                                        <small>{{ item.equipment_ip || item.device_id || "-" }}</small>
                                    </div>
                                </template>
                                <template v-slot:[`item.created_at`]="{ item }">
                                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="5">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Últimas presenças</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="attendanceHeaders"
                                :items="attendances"
                                :items-per-page="8"
                                hide-default-footer
                                class="app-table"
                                no-data-text="Nenhuma presença registrada"
                            >
                                <template v-slot:[`item.presence`]="{ item }">
                                    <v-chip label small :color="item.presence ? 'success' : 'error'" text-color="white">
                                        {{ item.presence ? "Presente" : "Ausente" }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.course_name`]="{ item }">
                                    <div class="stacked-cell">
                                        <strong>{{ item.course_name || "Componente" }}</strong>
                                        <small>{{ item.start_time || "--" }} - {{ item.end_time || "--" }}</small>
                                    </div>
                                </template>
                                <template v-slot:[`item.date`]="{ item }">
                                    {{ item.date | defaultDate("DD/MM/YYYY") }}
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" lg="7">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Avaliações e notas</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="assessmentHeaders"
                                :items="assessments"
                                :items-per-page="5"
                                class="app-table"
                                no-data-text="Nenhuma avaliação vinculada"
                                :footer-props="footerOptions"
                            >
                                <template v-slot:[`item.course`]="{ item }">
                                    {{ item.course ? item.course.name : "N/A" }}
                                </template>
                                <template v-slot:[`item.grade`]="{ item }">
                                    {{ item.grade && item.grade.grade ? item.grade.grade : "Sem nota" }}
                                </template>
                                <template v-slot:[`item.end_date`]="{ item }">
                                    {{ item.end_date | defaultDate("DD/MM/YYYY") }}
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" lg="5">
                    <v-card class="table-card">
                        <v-card-title class="table-card-title">Ocorrências recentes</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="occurrenceHeaders"
                                :items="occurrences"
                                :items-per-page="5"
                                hide-default-footer
                                class="app-table"
                                no-data-text="Nenhuma ocorrência registrada"
                            >
                                <template v-slot:[`item.description`]="{ item }">
                                    <div class="stacked-cell">
                                        <strong>{{ item.description || "Ocorrência" }}</strong>
                                        <small>{{ item.date | defaultDate("DD/MM/YYYY HH:mm") }}</small>
                                    </div>
                                </template>
                                <template v-slot:[`item.status`]="{ item }">
                                    <v-chip label small :color="item.seen_at ? 'success' : 'warning'" text-color="white">
                                        {{ item.seen_at ? "Lida" : "Pendente" }}
                                    </v-chip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script>
import axios from "axios";

export default {
    name: "StudentSummary",

    data: () => ({
        loading: false,
        student: {},
        exitPreference: null,
        schoolYear: {},
        enrollment: null,
        classData: {},
        answerables: [],
        transporters: [],
        movements: [],
        movementSummary: {},
        attendances: [],
        assessments: [],
        occurrences: [],
        stats: {},
        movementHeaders: [
            { text: "Tipo", value: "code" },
            { text: "Equipamento", value: "equipment" },
            { text: "Data", value: "created_at" },
        ],
        attendanceHeaders: [
            { text: "Componente", value: "course_name" },
            { text: "Status", value: "presence" },
            { text: "Data", value: "date" },
        ],
        assessmentHeaders: [
            { text: "Avaliação", value: "title" },
            { text: "Componente", value: "course" },
            { text: "Nota", value: "grade" },
            { text: "Entrega", value: "end_date" },
        ],
        occurrenceHeaders: [
            { text: "Ocorrência", value: "description" },
            { text: "Status", value: "status" },
        ],
        footerOptions: {
            itemsPerPageText: "Itens por página:",
            itemsPerPageOptions: [5, 10, 20],
            pageText: "{0}-{1} de {2}",
        },
    }),

    computed: {
        studentId() {
            return this.$route.params.id;
        },

        breadcrumbItems() {
            return [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Alunos", disabled: false, to: "/alunos", exact: true },
                { text: "Resumo do aluno", disabled: true, exact: true },
            ];
        },

        studentInitials() {
            const name = this.student.name || "Aluno";
            const parts = name.trim().split(/\s+/).filter(Boolean);
            return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "A";
        },

        enrollmentStatusLabel() {
            const status = this.enrollment && this.enrollment.status;
            const labels = {
                active: "Matrícula ativa",
                cancelled: "Matrícula cancelada",
                transferred: "Transferido",
                completed: "Concluído",
            };

            return labels[status] || "Sem matrícula ativa";
        },

        currentStatusLabel() {
            return this.movementSummary.current_status === "inside" ? "Dentro da escola" : "Fora da escola";
        },

        classEducationLabel() {
            return this.classData.education ? this.classData.education.name : "N/A";
        },

        enrollmentDateLabel() {
            if (!this.enrollment || !this.enrollment.enrollment_date && !this.enrollment.date) return "N/A";

            const date = this.enrollment.enrollment_date || this.enrollment.date;
            return this.$options.filters.defaultDate(date, "DD/MM/YYYY");
        },

        kpis() {
            return [
                { label: "Responsáveis", value: this.stats.answerables || 0, icon: "mdi-human-male-boy" },
                { label: "Transportes", value: this.stats.transporters || 0, icon: "mdi-bus-school" },
                { label: "Presença", value: `${this.stats.attendance_percent || 0}%`, icon: "mdi-calendar-check-outline" },
                { label: "Avaliações", value: this.stats.assessments || 0, icon: "mdi-clipboard-text-outline" },
                { label: "Média", value: this.stats.grade_average || "N/A", icon: "mdi-chart-line" },
                { label: "Ocorrências", value: this.stats.occurrences || 0, icon: "mdi-account-eye-outline" },
            ];
        },

        exitPreferenceLabel() {
            if (!this.exitPreference) return "CHEGUEI ativo";
            if (!this.exitPreference.responsible_enabled && !this.exitPreference.transporter_enabled) {
                return "Liberação por responsáveis e transporte desabilitada";
            }
            if (!this.exitPreference.transporter_enabled) return "Liberação via transporte escolar desabilitada";
            return "Liberação pelo responsável desabilitada";
        },
    },

    created() {
        this.load();
    },

    methods: {
        async load() {
            this.loading = true;

            try {
                const [result, preferenceResult] = await Promise.all([
                    axios.get(`/api/v1/students/${this.studentId}/summary`),
                    axios.get(`/api/v1/school-exit/students/${this.studentId}/preference`),
                ]);
                const data = result.data.content || {};

                this.exitPreference = preferenceResult.data.content || null;

                this.student = data.student || {};
                this.schoolYear = data.school_year || {};
                this.enrollment = data.enrollment || null;
                this.classData = data.class || {};
                this.answerables = data.answerables || [];
                this.transporters = data.transporters || [];
                this.movements = data.movements || [];
                this.movementSummary = data.movement_summary || {};
                this.attendances = data.attendances || [];
                this.assessments = data.assessments || [];
                this.occurrences = data.occurrences || [];
                this.stats = data.stats || {};
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 3000 });
            } finally {
                this.loading = false;
            }
        },

        movementLabel(code) {
            return Number(code) === 1001 ? "Entrada" : "Saída";
        },

        movementColor(code) {
            return Number(code) === 1001 ? "success" : "primary";
        },
    },
};
</script>

<style scoped lang="scss">
.summary-exit-note { display: block; margin-top: 4px; }
.student-summary-hero {
    align-items: flex-start;
    background: #0f172a !important;
    border-radius: 8px;
    color: #ffffff;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    min-width: 0;
    padding: 24px;
}

.student-hero-main {
    align-items: center;
    display: flex;
    gap: 18px;
    min-width: 0;
}

.student-hero-main > div {
    min-width: 0;
}

.student-hero-avatar {
    background: #eaf2ff;
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #0b63ce;
    font-size: 28px;
    font-weight: 900;
}

.student-summary-eyebrow {
    color: #93c5fd;
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.student-summary-hero h1 {
    color: #ffffff;
    font-size: 30px;
    font-weight: 900;
    line-height: 36px;
    margin: 0 0 8px;
    overflow-wrap: anywhere;
}

.student-summary-hero p {
    color: #cbd5e1;
    font-size: 14px;
    margin: 0;
    overflow-wrap: anywhere;
}

.student-summary-meta {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    min-width: 380px;
}

.summary-kpi-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
}

.student-summary-meta div,
.summary-kpi,
.summary-info-list div {
    background: #ffffff;
    border: 1px solid #e5eaf2;
    border-radius: 8px;
}

.student-summary-meta div {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.12);
    padding: 12px;
}

.student-summary-meta small,
.summary-info-list span,
.attendance-bars span {
    color: #94a3b8;
    display: block;
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 4px;
}

.student-summary-meta strong {
    color: #ffffff;
    font-size: 14px;
}

.summary-kpi {
    align-items: center;
    display: flex;
    gap: 14px;
    min-height: 86px;
    min-width: 0;
    padding: 16px;
}

.summary-kpi .v-icon {
    background: #edf4ff;
    border-radius: 8px;
    color: var(--v-primary-base);
    flex: 0 0 auto;
    padding: 8px;
}

.summary-kpi > div {
    min-width: 0;
}

.summary-kpi strong {
    color: #0f172a;
    display: block;
    font-size: 24px;
    font-weight: 900;
    overflow-wrap: anywhere;
}

.summary-kpi span {
    color: #64748b;
    display: block;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.summary-panel {
    min-height: 100%;
}

.summary-info-list {
    display: grid;
    gap: 10px;
}

.summary-info-list div {
    padding: 14px;
}

.summary-info-list strong {
    color: #0f172a;
    font-size: 15px;
}

.summary-chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.attendance-overview {
    margin-bottom: 18px;
}

.attendance-overview strong {
    color: var(--v-primary-base);
    display: block;
    font-size: 44px;
    font-weight: 900;
    line-height: 1;
}

.attendance-overview span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.attendance-bars {
    display: grid;
    gap: 10px;
}

.attendance-bars div {
    align-items: center;
    display: flex;
    justify-content: space-between;
}

.attendance-bars strong {
    color: #0f172a;
}

.stacked-cell strong,
.stacked-cell small {
    display: block;
}

.stacked-cell small {
    color: #64748b;
    font-size: 11px;
}

@media only screen and (max-width: 1260px) {
    .student-summary-hero {
        flex-direction: column;
    }

    .student-summary-meta {
        min-width: 0;
        width: 100%;
    }
}

@media only screen and (max-width: 960px) {
    .student-summary-hero,
    .student-hero-main {
        align-items: flex-start;
        flex-direction: column;
    }

    .student-summary-meta {
        grid-template-columns: 1fr;
        min-width: 0;
        width: 100%;
    }
}
</style>
